import { useEffect } from "react";
import { Typography, Box } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import HeroSection from "./components/HeroSection";
import BiographySection from "./components/BiographySection";
import CredentialsSection from "./components/CredentialsSection";

import { TEXTS } from "./constants";
import { extractPractitionerData } from "./utils";
import { LoadingContainer, NotFoundContainer, NotFoundButton } from "./styles";
import { Gapper } from "../../../ui/utils";
import { PageWrapper } from "../../../components/Layouts/PageWrapper";
import Seo from "../../../components/Seo";
import { buildPractitionerSeo } from "../../../utils/seoBuilders";
import {
  selectPractitionerByUrlName,
  selectPractitionersLoading,
  fetchPractitioners,
} from "../../../redux/reducers/practitioners.reducer";

const PractitionerProfile = () => {
  const { name } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const decodedName = decodeURIComponent(name);

  const userFromState = location.state?.user;
  const userFromStore = useSelector(selectPractitionerByUrlName(decodedName));
  const loading = useSelector(selectPractitionersLoading);

  // Prefer freshly-fetched store data over the router-state snapshot: the snapshot
  // is whatever the list held when the user clicked through, so it can be stale
  // (e.g. an hourlyRate / experience just edited in GHL). The snapshot is only an
  // instant placeholder until the refetch resolves.
  const user = userFromStore || userFromState;

  useEffect(() => {
    // Always refetch so the profile reflects the current GHL data, not a snapshot.
    dispatch(fetchPractitioners());
  }, [dispatch]);

  const practitionerData = extractPractitionerData(user, decodedName);

  if (loading && !userFromState) {
    return (
      <LoadingContainer>
        <Typography>{TEXTS.loading}</Typography>
      </LoadingContainer>
    );
  }

  if (!user) {
    return (
      <NotFoundContainer>
        <Box>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {TEXTS.notFound.title}
          </Typography>
          <Typography sx={{ mb: 3, textAlign: "center" }}>
            {TEXTS.notFound.description}
          </Typography>
          <NotFoundButton variant="contained" onClick={() => navigate("/")}>
            {TEXTS.notFound.button}
          </NotFoundButton>
        </Box>
      </NotFoundContainer>
    );
  }
  return (
    <PageWrapper>
      <Seo {...buildPractitionerSeo(practitionerData, location.pathname)} />
      <Gapper gap={100}>
        <HeroSection practitionerData={practitionerData} />
        <BiographySection
          fullBio={practitionerData.fullBio}
          firstName={practitionerData.firstName}
          fullName={practitionerData.fullName}
        />
        <CredentialsSection practitionerData={practitionerData} />
      </Gapper>
    </PageWrapper>
  );
};

export default PractitionerProfile;
